/**
 * @file ShopSettingsHubPage.tsx
 * @description «Настройки» — список разделов отдельным экраном (phase19.31,
 * SC15; page-analysis-c §2 макет (а), «360 — шаг 1»).
 *
 * Показывается ниже 1280px (телефон, планшет, 1024–1279): строка раздела
 * 64px — название 16/500, короткое пояснение и стрелка; нажатие открывает
 * раздел отдельным экраном (ссылка «назад» к списку — в `SettingsHubLayout`).
 * От 1280px каркас сразу открывает первый раздел рядом с колонкой разделов,
 * и эта страница не рисуется. Разделы и права — `visibleSettingsHub`
 * (один источник с колонкой). Ни одного доступного раздела — «Нет доступа».
 */

import { ChevronRight } from "lucide-react";
import { Link, Navigate } from "react-router";

import { useCurrentContext } from "../../../auth/hooks";
import { CabinetPageHeader } from "../../../components/ui/CabinetPageHeader";
import { itemHref, visibleSettingsHub } from "../../../lib/shop-nav";

export function ShopSettingsHubPage() {
  const ctx = useCurrentContext();
  const slug = ctx?.shop?.slug;
  const groups = visibleSettingsHub(ctx?.role.permissions ?? []);

  if (!slug || groups.length === 0) return <Navigate to="/403" replace />;

  return (
    <div className="flex flex-col gap-6" data-testid="settings-hub-list">
      <CabinetPageHeader title="Настройки" subtitle="Разделы настроек магазина" />
      {groups.map((group) => (
        <section key={group.id} aria-labelledby={`hub-${group.id}`} className="flex flex-col gap-2">
          <h2 id={`hub-${group.id}`} className="px-1 text-xs font-medium text-on-surface-variant">
            {group.label}
          </h2>
          <ul className="flex flex-col overflow-hidden rounded-xl bg-surface-lowest [box-shadow:inset_0_0_0_1px_var(--border)]">
            {group.items.map((item) => {
              const Icon = item.icon;
              return (
                <li key={item.id} className="border-b border-border last:border-b-0">
                  <Link
                    to={itemHref(slug, item)}
                    className="flex min-h-16 items-center gap-3 px-4 py-2 outline-none transition-colors hover:bg-surface-low focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring"
                  >
                    <Icon aria-hidden className="size-5 shrink-0 text-on-surface-variant" />
                    <span className="flex min-w-0 flex-1 flex-col">
                      <span className="text-base font-medium text-on-surface">{item.label}</span>
                      {item.description ? (
                        <span className="text-xs text-on-surface-variant">{item.description}</span>
                      ) : null}
                    </span>
                    <ChevronRight aria-hidden className="size-5 shrink-0 text-on-surface-variant" />
                  </Link>
                </li>
              );
            })}
          </ul>
        </section>
      ))}
    </div>
  );
}
